import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
import { Logger } from 'fsts';
import { namespace } from 'vuex-class';
import { CONST } from '@/shared/utils/Constants';

const name = 'admpNewsDocument-add-edit-dialog';
const logger = new Logger(name);
const admpNewsModule = namespace('admpNews');

@Component({ name })
export default class AdmpNewsDocumentAddEditDialog extends Vue {
  @Ref('fileInputRef') private fileInputRef!: HTMLInputElement;

  // Actions из Vuex
  @admpNewsModule.Action('uploadNewsDocument')
  private actionUploadNewsDocument!: any;
  @admpNewsModule.Action('updateAdmpNewsFile')
  private actionUpdateAdmpNewsFile!: any;
  @admpNewsModule.Action('getNewsFileDownloadLink')
  private actionGetNewsFileDownloadLink!: any;

  // Props
  @Prop({ default: false }) private dialog!: boolean;
  @Prop({ default: () => ({}) }) private value!: any;
  @Prop({ default: '' }) private admpNewsCreatedId!: string;

  // State
  fileName: string = '';
  selectedFile: File | null = null;
  isLoading: boolean = false;
  nameError: string = '';
  fileError: string = '';
  downloadUrl: string = '';

  get isEditMode(): boolean {
    if (!this.value) return false;
    const id = this.value.id || this.value.Id;
    const emptyGuid = CONST?.emptyGuid || '00000000-0000-0000-0000-000000000000';

    const hasValidId = !!id && id !== 0 && id !== '0' && id !== emptyGuid;
    const hasExistingFile = !!(
      this.value.filename ||
      this.value.FileName ||
      this.value.bucket_name ||
      this.value.BucketName
    );

    return hasValidId && hasExistingFile;
  }

  get admpNewsId() {
    return this.$route.params['admpNewsId'];
  }

  get canSave(): boolean {
    if (this.isLoading) return false;
    if (!this.fileName.trim()) return false;

    if (!this.isEditMode) {
      return !!this.selectedFile && !this.fileError;
    }

    return true;
  }

  @Watch('dialog')
  onDialogOpen(open: boolean) {
    if (open) {
      this.initForm();
    }
  }

  @Watch('value', { deep: true })
  onValueChange() {
    if (this.dialog) {
      this.initForm();
    }
  }

  created() {
    if (this.dialog) {
      this.initForm();
    }
  }

  private async initForm() {
    this.selectedFile = null;
    this.nameError = '';
    this.fileError = '';
    this.downloadUrl = '';
    if (this.fileInputRef) this.fileInputRef.value = '';

    this.fileName = this.value?.display_name || this.value?.DisplayName || this.value?.name || this.value?.Name || '';
  }

  private async fetchDownloadUrl(): Promise<string> {
    if (this.downloadUrl) return this.downloadUrl;

    try {
      const payload = {
        FileName: this.value.filename || this.value.FileName,
        BucketName: this.value.bucket_name || this.value.BucketName || 'admpnl-news-anlagen',
        DisplayName: this.fileName,
        OriginalName: this.value.original_name || this.value.OriginalName,
        IsSaveOnDisk: true,
      };

      const res = await this.actionGetNewsFileDownloadLink(payload);
      const url = typeof res === 'string' ? res : res?.url || res?.result || res?.data || '';
      if (url) {
        this.downloadUrl = url;
      }
      return url;
    } catch (e) {
      logger.warn('Download url could not be generated', e);
      return '';
    }
  }

  async downloadFile() {
    try {
      const payload = {
        FileName: this.value.filename || this.value.FileName,
        BucketName: this.value.bucket_name || this.value.BucketName || 'admpnl-news-anlagen',
        DisplayName: this.fileName,
        OriginalName: this.value.original_name || this.value.OriginalName,
        IsSaveOnDisk: true,
      };

      await this.actionGetNewsFileDownloadLink(payload);
    } catch (err) {
      logger.error('Error downloading file:', err);
    }
  }

  triggerFileInput() {
    this.fileInputRef?.click();
  }

  clearSelectedFile() {
    this.selectedFile = null;
    this.fileError = '';
    if (this.fileInputRef) this.fileInputRef.value = '';
  }

  onFileChange(event: Event) {
    this.fileError = '';
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
        this.fileError = 'Nur PDF-Dateien sind erlaubt.';
        this.selectedFile = null;
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.fileError = 'Die Datei darf maximal 5 MB groß sein.';
        this.selectedFile = null;
        return;
      }

      this.selectedFile = file;

      if (!this.fileName.trim()) {
        this.fileName = file.name.replace(/\.[^/.]+$/, '');
      }
    }
  }

  formatFileSize(bytes?: number): string {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async onSave() {
    if (!this.canSave) return;

    const newsId = this.admpNewsCreatedId || this.admpNewsId;
    this.isLoading = true;
    try {
      if (this.isEditMode) {
        const currentName = (this.value.display_name || this.value.DisplayName || '').trim();
        if (currentName !== this.fileName.trim()) {
          const payload = {
            DisplayName: this.fileName.trim(),
            AdmpNewsFileId: this.value.id || this.value.Id,
          };
          const result = await this.actionUpdateAdmpNewsFile(payload);
          this.$emit('click:update', result?.result ?? { ...this.value, display_name: this.fileName.trim() });
        } else {
          this.$emit('click:update', this.value);
        }
        this.onClickedClose();
      } else {
        if (!this.selectedFile) return;

        const payload = {
          document: this.selectedFile,
          FileName: this.fileName.trim() || this.selectedFile.name,
          BucketName: 'admpnl-news-anlagen',
          AdmpNewsId: newsId,
          id: 0,
        };

        const result = await this.actionUploadNewsDocument(payload);

        if (result?.isValid || result?.result) {
          this.$emit('click:update', result.result);
        } else if (result?.id) {
          this.$emit('click:update', result);
        } else {
          this.$emit('click:update', result);
        }

        this.onClickedClose();
      }
    } catch (e) {
      logger.error('Error saving news document:', e);
    } finally {
      this.isLoading = false;
    }
  }

  onClickedClose() {
    this.selectedFile = null;
    this.fileError = '';
    this.nameError = '';
    this.downloadUrl = '';
    this.$emit('click:close', { updateList: true });
  }
}
